import React, { useContext, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useMutationLock } from "../hooks/useMutationLock";
import PaginationFooter from "../components/PaginationFooter";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import * as gradesApi from "../api/grades";

export default function AdminGrades() {
  const [grades, setGrades] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const limit = DEFAULT_PAGE_SIZE;
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_grades");
  const { hasPermission: canEdit } = useViewPermission("edit_grades");
  const { busy, run } = useMutationLock();

  const fetchGrades = async (pageArg = page) => {
    try {
      const response = await gradesApi.list({ page: pageArg, limit });
      const normalized = normalizeListResponse(response.data, pageArg, limit);
      setGrades(normalized.data);
      setPage(normalized.page);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages);
    } catch (err) {
      console.error("Error fetching grades:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchGrades(newPage);
  };

  useEffect(() => {
    if (!hasPermission) return;
    fetchGrades(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  const columnKeys = ["id", "name"];

  const [name, setName] = useState(null);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    await run(async () => {
      try {
        if (!name) {
          notificationCtx.show({
            message: "Please add a name.",
          });
          return;
        }
        await gradesApi.create({ name });
        setPage(1);
        await fetchGrades(1);
        setName("");
        setShowGradeForm(false);
        notificationCtx.show({
          message: "Grade added successfully!",
          stat: "success",
        });
      } catch (error) {
        console.error("Error adding grade:", error);
        notificationCtx.show({
          message: "Failed to add grade.",
          stat: "error",
        });
      }
    });
  };

  const handleDeleteGrade = (gradeId) => {
    if (!canEdit) return;
    const confirmDeleteGrade = async () => {
      try {
        await gradesApi.remove(gradeId);

        const nextPage =
          grades.length <= 1 && page > 1 ? page - 1 : page;
        setPage(nextPage);
        await fetchGrades(nextPage);
        notificationCtx.show({
          message: "Grade deleted successfully",
          stat: "success",
        });
      } catch (error) {
        console.error("Delete failed:", error);
        notificationCtx.show({
          message: "Failed to delete grade",
          stat: "error",
        });
      }
    };

    notificationCtx.show({
      message: "Are you sure you want to delete this grade?",
      stat: "info",
      onConfirm: confirmDeleteGrade,
    });
  };

  const [showGradeForm, setShowGradeForm] = useState(false);

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      {isAuthenticated && hasPermission && (
        <div
          className="d-flex flex-column"
          style={{
            height: "109vh",
            minHeight: 0,
            padding: "10px 50px 0",
            overflow: "hidden",
          }}
        >
          {/* ================= TOP CONTROLS ================= */}
          <div
            style={{
              backgroundColor: "#d5d1fd",
              borderRadius: "20px",
              padding: "26px 30px",
              flexShrink: 0,
              boxShadow: "0 2px 10px rgba(48,45,69,0.04)",
            }}
          >
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                marginBottom: "18px",
              }}
            >
              {/* Heading */}
              <div>
                <h1
                  className="mb-1"
                  style={{
                    // fontSize: "28px",
                    fontWeight: "700",
                    color: "#302d45",
                  }}
                >
                  ALL GRADES
                </h1>

                <small
                  style={{
                    color: "#625d75",
                    fontSize: "13px",
                  }}
                >
                  Manage the grades available in the system
                </small>
              </div>

              {/* Actions */}
              <div className="d-flex align-items-center gap-2">
                {canEdit && (
                  <button
                    className="btn"
                    onClick={() => setShowGradeForm(true)}
                    style = {{
                      height: "38px",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "#fff",
                      color: "#403b55",
                      border: "none",
                      padding: "0 15px",
                      borderRadius: "8px",
                      fontWeight: "600",
                      fontSize: "13px",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    + Add Grade
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ================= SPACING ================= */}
          <div style={{ height: "16px", flexShrink: 0 }} />

          {/* ================= TABLE ================= */}

          {grades.length === 0 ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{
                flex: 1,
                minHeight: 0,
                color: "#77718b",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#403b55",
                }}
              >
                No grades available
              </div>

              <small
                style={{
                  marginTop: "5px",
                  fontSize: "13px",
                }}
              >
                Add a grade using the button above.
              </small>
            </div>
          ) : (
            <>
              {/* ================= TABLE ================= */}
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                  marginTop: "10px",
                  backgroundColor: "#fff",
                  borderRadius: "14px",
                  border: "1px solid #eeeaf8",
                  boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    overflow: "auto",
                  }}
                >
                  <table
                    className="table align-middle mb-0"
                    style={{
                      minWidth: "700px",
                      width: "100%",
                      background: "#fff",
                      borderCollapse: "separate",
                      borderSpacing: 0,
                    }}
                  >
                    <thead>
                      <tr>
                        {columnKeys.map((key, idx) => (
                          <th
                            key={idx}
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 5,
                              background: "#d5d1fd",
                              color: "#302d45",
                              fontWeight: "600",
                              padding: "13px 14px",
                              textAlign: "center",
                              letterSpacing: "0.4px",
                              fontSize: "13px",
                              borderBottom: "1px solid #e6e3f3",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {key.toUpperCase()}
                          </th>
                        ))}

                        <th
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 5,
                            background: "#d5d1fd",
                            color: "#302d45",
                            fontWeight: "600",
                            padding: "13px 14px",
                            textAlign: "center",
                            letterSpacing: "0.4px",
                            fontSize: "13px",
                            borderBottom: "1px solid #e6e3f3",
                            width: "110px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          ACTIONS
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {grades.map((item, index) => (
                        <tr key={index}>
                          {columnKeys.map((key) => (
                            <td
                              key={key}
                              style={{
                                background: "#fff",
                                padding: "12px 14px",
                                textAlign: "center",
                                fontSize: "14px",
                                color: "#403b55",
                                borderBottom: "1px solid #f0edf6",
                                whiteSpace: "nowrap",
                                verticalAlign: "middle",
                              }}
                            >
                              {item[key]}
                            </td>
                          ))}

                          <td
                            style={{
                              textAlign: "center",
                              padding: "10px 12px",
                              borderBottom: "1px solid #f0edf6",
                            }}
                          >
                            {canEdit && (
                              <button
                                className="btn"
                                onClick={() => handleDeleteGrade(item.id)}
                                style={{
                                  backgroundColor: "#f2c4c4",
                                  border: "none",
                                  borderRadius: "8px",
                                  width: "38px",
                                  height: "34px",
                                  padding: 0,
                                }}
                              >
                                <i
                                  className="fa-solid fa-trash"
                                  style={{
                                    fontSize: "14px",
                                    color: "#654343",
                                  }}
                                />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ================= FIXED FOOTER ================= */}
              <PaginationFooter
                page={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemLabel="grades"
              />
            </>
          )}

          {/* ================= ADD GRADE MODAL ================= */}
          {canEdit && showGradeForm && (
            <div
              className="modal show"
              style={{
                display: "block",
                backgroundColor: "rgba(48,45,69,0.45)",
                zIndex: 1050,
              }}
              tabIndex="-1"
            >
              <div
                className="modal-dialog modal-dialog-centered"
                style={{
                  maxWidth: "460px",
                }}
              >
                <div
                  className="modal-content"
                  style={{
                    border: "none",
                    borderRadius: "18px",
                    overflow: "hidden",
                    boxShadow: "0 10px 35px rgba(48,45,69,0.15)",
                  }}
                >
                  {/* Modal Header */}
                  <div
                    style={{
                      backgroundColor: "#d5d1fd",
                      padding: "22px 24px",
                      position: "relative",
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        fontWeight: "700",
                        color: "#302d45",
                        fontSize: "20px",
                      }}
                    >
                      ADD GRADE
                    </h4>

                    <small
                      style={{
                        color: "#625d75",
                        fontSize: "13px",
                      }}
                    >
                      Add a new grade to the system.
                    </small>

                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowGradeForm(false)}
                      style={{
                        position: "absolute",
                        top: "22px",
                        right: "22px",
                      }}
                      aria-label="Close"
                    />
                  </div>

                  {/* Modal Body */}
                  <div
                    style={{
                      padding: "24px",
                      backgroundColor: "#fff",
                    }}
                  >
                    <form onSubmit={handleSubmit}>
                      <div className="mb-3">
                        <label
                          htmlFor="gradeName"
                          style={{
                            display: "block",
                            marginBottom: "7px",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#403b55",
                          }}
                        >
                          Grade Name
                        </label>

                        <input
                          id="gradeName"
                          type="text"
                          name="grade"
                          value={name || ""}
                          onChange={(e) => setName(e.target.value)}
                          required
                          className="form-control"
                          placeholder="Enter Grade Name"
                          style={{
                            border: "1px solid #e8e5f0",
                            borderRadius: "9px",
                            padding: "10px 12px",
                            fontSize: "14px",
                            color: "#403b55",
                          }}
                        />
                      </div>

                      <button
                        type="submit"
                        className="btn w-100"
                        disabled={busy || !name}
                        style={{
                          backgroundColor: "#f2c4c4",
                          border: "none",
                          borderRadius: "8px",
                          padding: "10px 14px",
                          color: "#654343",
                          fontSize: "13px",
                          fontWeight: "600",
                        }}
                      >
                        {busy ? "Working..." : "ADD GRADE"}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
